import { captureError } from "@/lib/sentry";

interface LogErrorOptions {
  extra?: Record<string, unknown>;
  /** Forzar envío a Sentry aunque el beforeSend genérico lo filtraría.
   *  Usar SOLO en paths críticos (pagos, transferencias, registración a eventos)
   *  donde un error silencioso le cuesta plata al usuario. */
  forceSentry?: boolean;
  /** Tag opcional para agrupar en Sentry. Ej "transfer", "checkout", "strava". */
  tag?: string;
}

export const logError = (
  context: string,
  error: unknown,
  options?: LogErrorOptions | Record<string, unknown>,
): void => {
  // Backwards compat: la signature vieja era logError(context, error, extra).
  // Si pasaron un objeto plano (no LogErrorOptions), lo tratamos como extra.
  const opts: LogErrorOptions =
    options && ("extra" in options || "forceSentry" in options || "tag" in options)
      ? (options as LogErrorOptions)
      : { extra: options as Record<string, unknown> | undefined };

  if (import.meta.env.DEV) {
    console.error(`[${context}]`, error, opts.extra);
    return;
  }

  const message = error instanceof Error ? error.message : String(error);
  console.error(`[${context}] ${message}`);

  captureError(error, {
    context,
    ...(opts.tag ? { errorTag: opts.tag } : {}),
    ...(opts.extra ?? {}),
    ...(opts.forceSentry ? { __force_sentry: true } : {}),
  });
};

/**
 * Codigos PostgreSQL/PostgREST que nos importan para mensajes específicos al user.
 * Ver https://www.postgresql.org/docs/current/errcodes-appendix.html
 */
const PG_ERROR_MESSAGES: Record<string, string> = {
  // 23xxx — Integrity Constraint Violation
  "23502": "Falta un dato obligatorio. Refrescá la página e intentá de nuevo.",
  "23503": "No se puede completar la operación porque depende de datos que no existen.",
  "23505": "Ese registro ya existe. Verificá si no lo creaste antes.",
  "23514":
    "Datos inválidos para esta operación. Es un bug nuestro — te pedimos que escribas al equipo.",
  // 42xxx — Syntax / Permission
  "42501": "No tenés permiso para esta acción. Verificá que estés iniciada sesión.",
  // 28xxx — Auth
  "28P01": "Tu sesión expiró. Volvé a iniciar sesión.",
  // PostgREST PGRSTxxx
  PGRST301: "Tu sesión expiró. Volvé a iniciar sesión.",
  PGRST116: "No encontramos lo que buscás. Quizás se eliminó.",
};

/** Extrae un código (PostgreSQL/PostgREST) si la error trae uno. */
const extractCode = (error: unknown): string | null => {
  if (error && typeof error === "object") {
    const obj = error as Record<string, unknown>;
    if (typeof obj.code === "string" && obj.code.length > 0) return obj.code;
  }
  return null;
};

/**
 * Get a user-friendly error message
 * @param error - The error object
 * @returns A sanitized message safe to show to users
 */
export const getUserMessage = (error: unknown): string => {
  // 1) Códigos estructurados de Postgres/PostgREST primero
  const code = extractCode(error);
  if (code && PG_ERROR_MESSAGES[code]) return PG_ERROR_MESSAGES[code];

  // 2) Patterns de mensaje conocidos
  if (error instanceof Error) {
    const m = error.message;

    // Supabase auth
    if (m.includes("Invalid login credentials")) return "Email o contraseña incorrectos";
    if (m.includes("Email not confirmed"))
      return "Por favor, confirmá tu email antes de iniciar sesión";
    if (m.includes("User already registered")) return "Ya existe una cuenta con ese email";
    if (m.includes("Password should be"))
      return "La contraseña debe tener al menos 6 caracteres";

    // Sesión / autorización
    if (m.includes("No authorization header"))
      return "No pudimos validar tu sesión. Cerrá sesión e iniciá nuevamente.";
    if (m.includes("Tu sesión expiró"))
      return "Tu sesión expiró. Volvé a iniciar sesión para continuar.";
    if (m.includes("JWT expired") || m.includes("jwt expired"))
      return "Tu sesión expiró. Volvé a iniciar sesión.";

    // MercadoPago
    if (m.includes("Mercado Pago error"))
      return "Mercado Pago rechazó la operación. Revisá los datos de la cuenta e intentá otra vez.";

    // Storage upload
    if (m.includes("mime type") && m.includes("not supported"))
      return "Formato de archivo no soportado. Subí una foto JPG/PNG o un PDF.";
    if (m.includes("Payload too large") || m.includes("exceeded the maximum allowed size"))
      return "La imagen pesa demasiado. Reducí el tamaño (máx 5MB).";
    if (m.includes("new row violates row-level security"))
      return "No tenés permiso para subir este archivo. Verificá que estés logueada con la cuenta correcta.";

    // RPC del flow de transferencia (devueltos como result.error desde el RPC)
    if (m === "order_not_found") return "No encontramos tu orden. Volvé a Eventos e intentá de nuevo.";
    if (m === "not_transfer_order")
      return "Esta orden no es por transferencia. Recargá la página.";
    if (m === "invalid_status")
      return "Esta orden ya no acepta comprobantes (probablemente ya fue revisada).";
    if (m === "transfer_not_available")
      return "Este evento dejó de aceptar transferencias. Probá con Mercado Pago.";
    if (m === "unauthenticated")
      return "Tu sesión expiró. Volvé a iniciar sesión.";
  }

  // 3) Default genérico
  return "Ocurrió un error. Por favor, intentá de nuevo.";
};

/**
 * Combina logError + getUserMessage. Se usa típicamente desde un catch:
 *   const msg = reportError("transfer-submit-proof", err, { orderId });
 *   toast.error(msg);
 */
export const reportError = (
  context: string,
  error: unknown,
  options?: LogErrorOptions,
): string => {
  logError(context, error, options);
  return getUserMessage(error);
};
